import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
    width: 100%;
    font-family: var(--main-font-family);
`;

const SummaryLine = styled.div`
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--light-gray);
    margin-bottom: 0.75rem;

    > span:first-of-type {
        font-weight: 800;
        color: var(--gray);
    }
`;

const HighlightValue = styled.span`
    font-weight: 800;
    color: ${props => {
        if (props.$error) return 'var(--red, #c0392b)';
        if (props.$warn) return 'var(--yellow-alert-color)';
        return 'var(--gray)';
    }};
`;

const TableScroll = styled.div`
    width: 100%;
    overflow-x: auto;
`;

const MatrixTable = styled.table`
    width: 100%;
    min-width: 18rem;
    border-collapse: collapse;
    font-size: 0.9rem;

    th, td {
        padding: 0.55rem 0.65rem;
        text-align: center;
        border-bottom: 1px solid var(--lighter-gray, #e8e8e8);
    }

    th {
        font-weight: 700;
        color: var(--gray);
        background: var(--bg);
        white-space: nowrap;
    }

    th:first-child,
    td:first-child {
        text-align: left;
        font-weight: 700;
        color: var(--dark-gray, #333);
        position: sticky;
        left: 0;
        background: var(--bg);
        z-index: 1;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
`;

const CellInput = styled.input`
    padding: 0.25rem 0.4rem;
    width: 5.5rem;
    font-weight: 600;
    font-family: var(--main-font-family);
    font-size: 0.9rem;
    border: none;
    color: var(--gray);
    text-align: center;
    box-shadow: var(--button-outline);
    -moz-box-shadow: var(--button-outline);
    -webkit-box-shadow: var(--button-outline);
    border-radius: 0.4rem;
    background: var(--bg);

    &:focus {
        outline: rgba(48, 192, 140, 0.5) solid 0.25rem !important;
    }
`;

const AmountDisplay = styled.span`
    font-weight: 800;
    color: var(--gray);
`;

const EDITOR_ID = 'rateAllocationMatrixEditor';
const SUBMIT_BLOCKED_EVENT = 'rateAllocationMatrixSubmitBlocked';
const MONEY_EPSILON = 0.01;

function formatMoney(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0';
    return num.toFixed(2).replace(/\.?0+$/, '');
}

function formatWeekHeader(weekStartDate) {
    if (!weekStartDate) return '—';
    const startDate = new Date(`${weekStartDate}T00:00`);
    if (Number.isNaN(startDate.getTime())) return weekStartDate;
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    return `${startDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}`;
}

function cellKey(truckId, weekStartDate) {
    return `${truckId}|${weekStartDate}`;
}

function sanitizeMoneyInput(raw) {
    if (raw == null) return '';
    let cleaned = String(raw).replace(/[^\d.]/g, '');
    const dotIndex = cleaned.indexOf('.');
    if (dotIndex !== -1) {
        cleaned = cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '');
    }
    return cleaned;
}

function parseMoneyValue(raw) {
    const cleaned = sanitizeMoneyInput(raw);
    if (cleaned === '' || cleaned === '.') return 0;
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function buildMatrixCellAmounts({
    rateAllocationMatrix,
    loadExecutors,
    rateDistributionByWeeks,
    amountForInvoice,
}) {
    const weeks = (rateDistributionByWeeks || []).map(w => w.weekStartDate).filter(Boolean);
    const trucks = (loadExecutors || []).filter(e => e?.id != null);
    const amounts = {};
    const total = Number(amountForInvoice) || 0;

    if (rateAllocationMatrix && rateAllocationMatrix.length > 0) {
        for (const cell of rateAllocationMatrix) {
            if (cell?.truckId == null || !cell?.weekStartDate) continue;
            amounts[cellKey(cell.truckId, cell.weekStartDate)] = Math.round(total * (cell.percentage || 0));
        }
    } else {
        const allExecutorsHaveNullPercentage = trucks.length > 0 && trucks.every(truck => {
            const pct = truck.percentage;
            return pct == null || pct === '';
        });
        const lastTruckId = trucks.length > 0 ? trucks[trucks.length - 1].id : null;

        for (const truck of trucks) {
            const truckPct = allExecutorsHaveNullPercentage
                ? (truck.id === lastTruckId ? 1 : 0)
                : (truck.percentage ?? 0);
            for (const week of rateDistributionByWeeks || []) {
                if (!week?.weekStartDate) continue;
                const weekPct = week.percentage ?? 0;
                amounts[cellKey(truck.id, week.weekStartDate)] = Math.round(total * truckPct * weekPct);
            }
        }
    }

    for (const truck of trucks) {
        for (const weekStart of weeks) {
            const key = cellKey(truck.id, weekStart);
            if (amounts[key] === undefined) amounts[key] = 0;
        }
    }

    return { amounts, weeks, trucks };
}

export function amountsToRateAllocationMatrix(amounts, trucks, weeks, amountForInvoice) {
    const total = Number(amountForInvoice) || 0;
    if (total <= 0) return [];

    const matrix = [];
    for (const truck of trucks) {
        for (const weekStart of weeks) {
            const amount = parseMoneyValue(amounts[cellKey(truck.id, weekStart)]);
            if (amount > 0) {
                matrix.push({
                    truckId: truck.id,
                    weekStartDate: weekStart,
                    percentage: amount / total,
                });
            }
        }
    }
    return matrix;
}

export function validateMatrixAllocation(amounts, trucks, weeks, amountForInvoice) {
    const total = Number(amountForInvoice) || 0;
    let allocated = 0;
    for (const truck of trucks) {
        for (const weekStart of weeks) {
            allocated += parseMoneyValue(amounts[cellKey(truck.id, weekStart)]);
        }
    }

    if (allocated - total > MONEY_EPSILON) {
        return {
            valid: false,
            allocated,
            unallocated: total - allocated,
            message: `Allocated amount ($${formatMoney(allocated)}) exceeds load rate ($${formatMoney(total)}).`,
        };
    }
    if (total - allocated > MONEY_EPSILON) {
        return {
            valid: false,
            allocated,
            unallocated: total - allocated,
            message: `Unallocated amount: $${formatMoney(total - allocated)}. The full load rate must be distributed.`,
        };
    }
    return { valid: true, allocated, unallocated: 0, message: null };
}

export const RateAllocationMatrix = ({
    viewMode = false,
    rateAllocationMatrix = null,
    loadExecutors = [],
    rateDistributionByWeeks = [],
    amountForInvoice = 0,
    data,
}) => {
    const invoiceAmount = amountForInvoice || data?.amountForInvoice || 0;

    const initial = useMemo(
        () => buildMatrixCellAmounts({
            rateAllocationMatrix: rateAllocationMatrix ?? data?.rateAllocationMatrix ?? null,
            loadExecutors: loadExecutors?.length ? loadExecutors : (data?.loadExecutors || []),
            rateDistributionByWeeks: rateDistributionByWeeks?.length ? rateDistributionByWeeks : (data?.rateDistributionByWeeks || []),
            amountForInvoice: invoiceAmount,
        }),
        [rateAllocationMatrix, loadExecutors, rateDistributionByWeeks, invoiceAmount, data]
    );

    const [amounts, setAmounts] = useState(initial.amounts);
    const [draftValues, setDraftValues] = useState({});
    const [submitBlocked, setSubmitBlocked] = useState(false);

    useEffect(() => {
        setAmounts(initial.amounts);
        setDraftValues({});
        setSubmitBlocked(false);
    }, [initial.amounts]);

    useEffect(() => {
        if (viewMode) return undefined;
        const container = document.getElementById(EDITOR_ID);
        const onSubmitBlocked = () => setSubmitBlocked(true);
        container?.addEventListener(SUBMIT_BLOCKED_EVENT, onSubmitBlocked);
        return () => container?.removeEventListener(SUBMIT_BLOCKED_EVENT, onSubmitBlocked);
    }, [viewMode]);

    const { weeks, trucks } = initial;

    const validation = useMemo(
        () => validateMatrixAllocation(amounts, trucks, weeks, invoiceAmount),
        [amounts, trucks, weeks, invoiceAmount]
    );

    const syncEditorMeta = useCallback((nextAmounts, nextValidation) => {
        if (viewMode) return;
        const container = document.getElementById(EDITOR_ID);
        if (!container) return;
        const matrixJson = JSON.stringify(
            amountsToRateAllocationMatrix(nextAmounts, trucks, weeks, invoiceAmount)
        );
        container.setAttribute('data-matrix-json', matrixJson);
        if (nextValidation.valid) {
            container.removeAttribute('data-validation-error');
        } else {
            container.setAttribute('data-validation-error', nextValidation.message);
        }
    }, [viewMode, trucks, weeks, invoiceAmount]);

    useEffect(() => {
        syncEditorMeta(amounts, validation);
    }, [amounts, validation, syncEditorMeta]);

    const displayValue = (truckId, weekStart) => {
        const key = cellKey(truckId, weekStart);
        if (!viewMode && draftValues[key] !== undefined) {
            return draftValues[key];
        }
        return formatMoney(amounts[key] || 0);
    };

    const commitCell = (truckId, weekStart, rawValue) => {
        const key = cellKey(truckId, weekStart);
        const parsed = parseMoneyValue(rawValue);
        const nextAmounts = { ...amounts, [key]: Math.round(parsed) };

        setSubmitBlocked(false);
        setAmounts(nextAmounts);
        setDraftValues(prev => {
            const copy = { ...prev };
            delete copy[key];
            return copy;
        });
    };

    const handleBlur = (truckId, weekStart, rawValue) => {
        commitCell(truckId, weekStart, rawValue);
    };

    const handleChange = (truckId, weekStart, rawValue) => {
        const key = cellKey(truckId, weekStart);
        setSubmitBlocked(false);
        setDraftValues(prev => ({ ...prev, [key]: sanitizeMoneyInput(rawValue) }));
    };

    if (!trucks.length || !weeks.length) {
        return null;
    }

    const hasUnallocated = validation.unallocated > MONEY_EPSILON;
    const isOverAllocated = validation.allocated - invoiceAmount > MONEY_EPSILON;
    const showAllocationWarning = !validation.valid && (hasUnallocated || isOverAllocated);
    const highlightWarn = showAllocationWarning && !submitBlocked;
    const highlightError = showAllocationWarning && submitBlocked;

    return (
        <Wrapper id={viewMode ? undefined : EDITOR_ID}>
            {!viewMode && (
                <SummaryLine>
                    Load rate: <span>${formatMoney(invoiceAmount)}</span>
                    {!validation.valid && (
                        <>
                            {' · '}
                            {hasUnallocated ? (
                                <>Unallocated: <HighlightValue $warn={highlightWarn} $error={highlightError}>${formatMoney(validation.unallocated)}</HighlightValue></>
                            ) : (
                                <>Over allocated by: <HighlightValue $warn={highlightWarn} $error={highlightError}>${formatMoney(validation.allocated - invoiceAmount)}</HighlightValue></>
                            )}
                        </>
                    )}
                </SummaryLine>
            )}
            <TableScroll>
                <MatrixTable>
                    <thead>
                        <tr>
                            <th>Truck</th>
                            {weeks.map(weekStart => (
                                <th key={weekStart}>{formatWeekHeader(weekStart)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {trucks.map(truck => (
                            <tr key={truck.id}>
                                <td>#{truck.truckNumber || truck.id}</td>
                                {weeks.map(weekStart => {
                                    const key = cellKey(truck.id, weekStart);
                                    return (
                                        <td key={key}>
                                            {viewMode ? (
                                                <AmountDisplay>${displayValue(truck.id, weekStart)}</AmountDisplay>
                                            ) : (
                                                <>
                                                    $&nbsp;
                                                    <CellInput
                                                        value={displayValue(truck.id, weekStart)}
                                                        onChange={(e) => handleChange(truck.id, weekStart, e.target.value)}
                                                        onBlur={(e) => handleBlur(truck.id, weekStart, e.target.value)}
                                                        inputMode="decimal"
                                                        autoComplete="off"
                                                    />
                                                </>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </MatrixTable>
            </TableScroll>
        </Wrapper>
    );
};

export function processRateAllocationMatrixChanges(column) {
    const container = document.getElementById(EDITOR_ID);
    if (!container) return null;

    const validationError = container.getAttribute('data-validation-error');
    if (validationError) {
        container.dispatchEvent(new CustomEvent(SUBMIT_BLOCKED_EVENT));
        return null;
    }

    const raw = container.getAttribute('data-matrix-json');
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        return { rateAllocationMatrix: parsed };
    } catch (e) {
        return null;
    }
}
